import { BookOpen, Check, Lock, Printer } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { PageHead } from '@/admin-area/ui/PageHead';
import { StateMessage } from '@/admin-area/ui/StateMessage';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import type { RuleVersion, RulesReply } from '../api-types';
import { formatDate } from '../format';
import '../archive.css';
import { HistoryList } from './HistoryList';
import { RuleSections } from './RuleSections';
import {
  GENERAL,
  areaFrom,
  chipsOf,
  inArea,
  isEmptyArea,
  isEnabled,
  scopeOfSection,
} from './rules-model';
import { TargetContext } from './target';
import { useRules } from './use-rules';

function AreaChips({
  reply,
  area,
  onPick,
}: {
  readonly reply: RulesReply;
  readonly area: string;
  readonly onPick: (area: string) => void;
}) {
  const { t } = useTranslation('archive');
  return (
    <div
      role="group"
      aria-label={t('rules.filter')}
      className="archive-no-print flex flex-wrap gap-2"
    >
      {chipsOf(reply).map((chip) => {
        const on = chip === area;
        // Selected = a solid fill AND a check (U16: never by colour alone); the pale tint it had
        // before read as unselected on a phone.
        return (
          <button
            key={chip}
            type="button"
            aria-pressed={on}
            onClick={() => onPick(chip)}
            className={`inline-flex min-h-11 items-center gap-1.5 rounded-full border px-4 text-sm font-semibold ${on ? 'border-[var(--brand)] bg-[var(--brand)] text-[var(--brand-foreground)]' : 'border-border bg-card'}`}
          >
            {on ? <Check aria-hidden="true" data-testid="chip-check" className="size-4" /> : null}
            {chip === GENERAL ? t('rules.general') : t('rules.area', { code: chip })}
          </button>
        );
      })}
    </div>
  );
}

function VersionBanner({ reply }: { readonly reply: RulesReply }) {
  const { t } = useTranslation('archive');
  if (reply.status !== 'proposta') return null;
  return (
    <div className="bg-[var(--attention-tint)] text-[var(--attention-ink)] flex items-start gap-3 rounded-md p-4">
      <BookOpen aria-hidden="true" className="mt-0.5 size-5 shrink-0" />
      <div>
        <p className="font-bold">
          {t('rules.bannerTitle', { version: reply.version, date: formatDate(reply.date) })}
        </p>
        <p className="text-sm">{t('rules.bannerText')}</p>
      </div>
    </div>
  );
}

function AreaBody({ reply, area }: { readonly reply: RulesReply; readonly area: string }) {
  const { t } = useTranslation('archive');
  if (!isEnabled(reply, area)) {
    return <StateMessage icon={Lock} role="status" title={t('rules.notEnabled')} />;
  }
  const rules = inArea(reply, area);
  if (isEmptyArea(rules))
    return <p className="text-muted-foreground text-sm">{t('rules.nothing')}</p>;
  return <RuleSections rules={rules} />;
}

function Body({
  reply,
  history,
}: {
  readonly reply: RulesReply;
  readonly history: readonly RuleVersion[];
}) {
  const { t } = useTranslation('archive');
  const { hash } = useLocation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const area = areaFrom(params.get('area'));
  const target = decodeURIComponent(hash.replace(/^#/, ''));

  // An anchor that lives in another area switches the filter to it (when the caller holds it).
  useEffect(() => {
    if (target === '') return;
    const scope = scopeOfSection(reply, target, area);
    if (scope !== undefined && scope !== area) {
      void navigate({ search: scope === GENERAL ? '' : `?area=${scope}`, hash }, { replace: true });
    }
    // Only a new anchor or new data may switch the area; picking a chip must not undo itself.
  }, [target, reply]);

  // Arriving with #anchor scrolls to that section once it is on the page.
  useEffect(() => {
    if (target === '') return;
    document.getElementById(target)?.scrollIntoView({ block: 'start' });
  }, [target, reply, area]);

  const pick = (next: string) =>
    void navigate({ search: next === GENERAL ? '' : `?area=${next}` }, { replace: true });

  return (
    <TargetContext.Provider value={target}>
      <div className="archive-rules flex flex-col gap-4">
        <VersionBanner reply={reply} />
        <section className="bg-card border-border rounded-md border p-4">
          <h3 className="text-xl font-bold">
            {t('rules.versionTitle', { version: reply.version })}{' '}
            <span className="bg-muted rounded-full px-2 py-0.5 text-xs font-semibold">
              {t(reply.status === 'proposta' ? 'rules.proposal' : 'rules.approved')}
            </span>
          </h3>
          <p className="text-muted-foreground text-sm">
            {t('rules.versionDate', { date: formatDate(reply.date) })}{' '}
            {t(reply.inForce ? 'rules.inForce' : 'rules.notInForce')}
          </p>
        </section>
        <HistoryList versions={history} />
        <AreaChips reply={reply} area={area} onPick={pick} />
        <AreaBody reply={reply} area={area} />
      </div>
    </TargetContext.Provider>
  );
}

/** Regole visibili (#788): the version, its history and the rules of the area in view. */
export function RulesPage() {
  const { t } = useTranslation('archive');
  const { state, retry } = useRules();
  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title={t('rules.title')}
        summary={t('rules.summary')}
        actions={
          <Button variant="outline" className="archive-no-print" onClick={() => window.print()}>
            <Printer aria-hidden="true" className="size-4" />
            {t('rules.export')}
          </Button>
        }
      />
      {state.status === 'loading' ? (
        <SkeletonRegion label={t('common.loading')} className="flex flex-col gap-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-48 w-full" />
        </SkeletonRegion>
      ) : null}
      {state.status === 'error' ? (
        <StateMessage
          role="alert"
          tone="error"
          icon={BookOpen}
          title={t('rules.error')}
          action={
            <Button variant="outline" onClick={retry}>
              {t('common.retry')}
            </Button>
          }
        />
      ) : null}
      {state.status === 'ready' ? <Body reply={state.rules} history={state.history} /> : null}
    </div>
  );
}
